/**
 * StoreRequestsTab — store activation request management.
 * Renders one of three views:
 *   1. Hub selector (personal vs company) when storeBusinessType is null
 *   2. Personal stores list when storeBusinessType === 'personal'
 *   3. Company stores list when storeBusinessType === 'company'
 * Extracted from ManagePlatform.js (was ~680 inline lines).
 */
import {
  ClipboardList, Bell, Clock, Edit3, RotateCcw,
  Calendar, XCircle, CheckCircle, Loader2, Eye, User, Building2,
  Mail, Phone,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { STORE_TYPES, STORE_STATUS, getStoreTypeName } from '../../../components/manage-platform/constants';

// ---------- Variant-specific theming ----------
const VARIANTS = {
  personal: {
    icon: User,
    iconBg: 'bg-purple-500/20',
    iconBorder: 'border-purple-500/30',
    iconColor: 'text-purple-400',
    iconFill: 'text-purple-500',
    badgeBg: 'bg-purple-500/20',
    badgeText: 'text-purple-400',
    hoverBorder: 'hover:border-purple-500/50',
    calendarColor: 'text-purple-400',
    titleAr: 'تفعيل المتاجر الشخصية',
    titleEn: 'Individual Store Activation',
    subtitleAr: 'المتاجر الفردية',
    subtitleEn: 'Individual Stores',
    rowTypeAr: 'فردي',
    rowTypeEn: 'Individual',
    rowTypeColor: 'text-purple-400',
    testidNew: 'store-filter-new',
    testidNeedsEdit: 'store-filter-needs-edit',
    testidEdited: 'store-filter-edited',
  },
  company: {
    icon: Building2,
    iconBg: 'bg-teal-500/20',
    iconBorder: 'border-teal-500/30',
    iconColor: 'text-teal-400',
    iconFill: 'text-teal-500',
    badgeBg: 'bg-teal-500/20',
    badgeText: 'text-teal-400',
    hoverBorder: 'hover:border-teal-500/50',
    calendarColor: 'text-teal-400',
    titleAr: 'تفعيل متاجر الشركات',
    titleEn: 'Company Store Activation',
    subtitleAr: 'متاجر الشركات',
    subtitleEn: 'Company Stores',
    rowTypeAr: 'شركة',
    rowTypeEn: 'Company',
    rowTypeColor: 'text-teal-400',
    testidNew: 'store-filter-new-company',
    testidNeedsEdit: 'store-filter-needs-edit-company',
    testidEdited: 'store-filter-edited-company',
  },
};

// ---------- Hub view (personal vs company chooser) ----------
function HubView({ language, storeNotifications, setStoreBusinessType, setStatusFilter, getBusinessTypeCounts }) {
  return (
    <div data-testid="store-requests-hub">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-orange-500" />
          {language === 'ar' ? 'طلبات تفعيل المتاجر' : 'Store Activation Requests'}
          {storeNotifications.has_new && (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
            </span>
          )}
        </h2>
      </div>

      <p className="text-[#808080] mb-8 text-center">
        {language === 'ar' ? 'اختر نوع المتجر لعرض طلبات التفعيل' : 'Select store type to view activation requests'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <div
          onClick={() => {
            setStoreBusinessType('personal');
            setStatusFilter('new');
          }}
          data-testid="store-type-personal-btn"
          className="group bg-[#1a1a2e] rounded-2xl p-8 border border-[#2a2a4e] hover:border-purple-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-500/20"
        >
          <div className="text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border border-purple-500/30">
              <User className="h-12 w-12 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {language === 'ar' ? 'تفعيل المتاجر الشخصية' : 'Individual Store Activation'}
            </h3>
            <p className="text-sm text-[#808080] mb-4">
              {language === 'ar' ? 'المتاجر الفردية' : 'Individual Stores'}
            </p>
            {getBusinessTypeCounts().personalCount > 0 && (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
                <Bell className="h-4 w-4" />
                {getBusinessTypeCounts().personalCount} {language === 'ar' ? 'طلب' : 'requests'}
              </span>
            )}
          </div>
        </div>

        <div
          onClick={() => {
            setStoreBusinessType('company');
            setStatusFilter('new');
          }}
          data-testid="store-type-company-btn"
          className="group bg-[#1a1a2e] rounded-2xl p-8 border border-[#2a2a4e] hover:border-teal-500 transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:shadow-teal-500/20"
        >
          <div className="text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-teal-500/20 to-teal-500/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform border border-teal-500/30">
              <Building2 className="h-12 w-12 text-teal-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {language === 'ar' ? 'تفعيل متاجر الشركات' : 'Company Store Activation'}
            </h3>
            <p className="text-sm text-[#808080] mb-4">
              {language === 'ar' ? 'متاجر الشركات' : 'Company Stores'}
            </p>
            {getBusinessTypeCounts().companyCount > 0 && (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/20 text-teal-400 rounded-full text-sm font-medium">
                <Bell className="h-4 w-4" />
                {getBusinessTypeCounts().companyCount} {language === 'ar' ? 'طلب' : 'requests'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- List view (personal OR company) ----------
function ListView({ variant, language, props }) {
  const V = VARIANTS[variant];
  const Icon = V.icon;
  const {
    setStoreBusinessType, getFilteredStores, statusFilter, setStatusFilter,
    getStoreRequestCounts, typeFilter, setTypeFilter,
    storeRequestDateFrom, setStoreRequestDateFrom,
    storeRequestDateTo, setStoreRequestDateTo,
    loading, formatDate, processingStore, approveStore,
    setSelectedStore, setShowStoreDetails,
    setShowModificationModal, setShowRejectModal,
  } = props;

  const stores = getFilteredStores();

  return (
    <div data-testid={`store-requests-${variant}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 ${V.iconBg} rounded-xl flex items-center justify-center border ${V.iconBorder}`}>
            <Icon className={`h-6 w-6 ${V.iconColor}`} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {language === 'ar' ? V.titleAr : V.titleEn}
            </h2>
            <p className="text-sm text-[#808080]">
              {language === 'ar' ? V.subtitleAr : V.subtitleEn}
            </p>
          </div>
        </div>
        <span className={`${V.badgeBg} ${V.badgeText} px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1`}>
          <Bell className="h-4 w-4" />
          {stores.length} {language === 'ar' ? 'طلب' : 'requests'}
        </span>
      </div>

      {/* Status Switch */}
      <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
        <button
          onClick={() => setStatusFilter('new')}
          data-testid={V.testidNew}
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            statusFilter === 'new'
              ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
              : 'bg-[#0a0a0a] text-[#808080] hover:text-white hover:bg-[#2a2a4e]'
          }`}
        >
          <Clock className="h-4 w-4" />
          {language === 'ar' ? 'طلبات جديدة' : 'New Requests'}
          {getStoreRequestCounts().newCount > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${statusFilter === 'new' ? 'bg-white/20' : 'bg-blue-500/20 text-blue-400'}`}>
              {getStoreRequestCounts().newCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setStatusFilter('needs_edit')}
          data-testid={V.testidNeedsEdit}
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            statusFilter === 'needs_edit'
              ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30'
              : 'bg-[#0a0a0a] text-[#808080] hover:text-white hover:bg-[#2a2a4e]'
          }`}
        >
          <Edit3 className="h-4 w-4" />
          {language === 'ar' ? 'مطلوب تعديل' : 'Needs Edit'}
          {getStoreRequestCounts().needsEditCount > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${statusFilter === 'needs_edit' ? 'bg-white/20' : 'bg-amber-500/20 text-amber-400'}`}>
              {getStoreRequestCounts().needsEditCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setStatusFilter('edited')}
          data-testid={V.testidEdited}
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            statusFilter === 'edited'
              ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
              : 'bg-[#0a0a0a] text-[#808080] hover:text-white hover:bg-[#2a2a4e]'
          }`}
        >
          <RotateCcw className="h-4 w-4" />
          {language === 'ar' ? 'الطلبات المعدلة' : 'Edited Requests'}
          {getStoreRequestCounts().editedCount > 0 && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${statusFilter === 'edited' ? 'bg-white/20' : 'bg-emerald-500/20 text-emerald-400'}`}>
              {getStoreRequestCounts().editedCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
        <div className="flex items-center gap-2">
          <label className="text-[#808080] text-sm">
            {language === 'ar' ? 'نوع النشاط:' : 'Activity Type:'}
          </label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-[#0a0a0a] border border-[#2a2a4e] text-white px-3 py-2 rounded-xl text-sm"
          >
            <option value="all">{language === 'ar' ? 'جميع الأنواع' : 'All Types'}</option>
            {Object.entries(STORE_TYPES).map(([key, val]) => (
              <option key={key} value={key}>{val}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Calendar className={`h-4 w-4 ${V.calendarColor}`} />
          <label className="text-[#808080] text-sm">
            {language === 'ar' ? 'من:' : 'From:'}
          </label>
          <input
            type="date"
            value={storeRequestDateFrom}
            onChange={(e) => setStoreRequestDateFrom(e.target.value)}
            className="px-3 py-2 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[#808080] text-sm">
            {language === 'ar' ? 'إلى:' : 'To:'}
          </label>
          <input
            type="date"
            value={storeRequestDateTo}
            onChange={(e) => setStoreRequestDateTo(e.target.value)}
            className="px-3 py-2 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl text-white text-sm"
          />
        </div>

        {(storeRequestDateFrom || storeRequestDateTo || typeFilter !== 'all') && (
          <Button
            onClick={() => {
              setStoreRequestDateFrom('');
              setStoreRequestDateTo('');
              setTypeFilter('all');
            }}
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <XCircle className="h-4 w-4 me-1" />
            {language === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
          </Button>
        )}
      </div>

      {/* Store List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={`sr-skel-${variant}-${i}`} className="h-32 bg-[#0a0a0a] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : stores.length === 0 ? (
        <div className="text-center py-16 text-[#808080] bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
          <CheckCircle className="h-16 w-16 mx-auto mb-4 opacity-30 text-emerald-500" />
          <p className="text-lg">
            {language === 'ar' ? 'لا توجد طلبات في ' : 'No requests in '}
            {statusFilter === 'new' ? (language === 'ar' ? 'الطلبات الجديدة' : 'New Requests') :
              statusFilter === 'needs_edit' ? (language === 'ar' ? 'مطلوب تعديل' : 'Needs Edit') :
                statusFilter === 'edited' ? (language === 'ar' ? 'الطلبات المعدلة' : 'Edited Requests') :
                  (language === 'ar' ? 'هذا القسم' : 'this section')}
          </p>
          <p className="text-sm mt-2 text-[#606060]">
            {language === 'ar' ? 'جميع الطلبات تمت معالجتها' : 'All requests have been processed'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {stores.map((store) => {
            const status = STORE_STATUS[store.status] || STORE_STATUS.pending;
            const StatusIcon = status.icon;
            return (
              <div
                key={store.id}
                className={`p-5 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] ${V.hoverBorder} transition-all`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-xl ${V.iconBg} flex items-center justify-center`}>
                        <Icon className={`h-6 w-6 ${V.iconFill}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-white text-lg">
                            {store.name || store.nameEn || (language === 'ar' ? 'بدون اسم' : 'No Name')}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${status.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {language === 'ar' ? status.label : status.labelEn}
                          </span>
                        </div>
                        {store.nameEn && store.name && (
                          <p className="text-sm text-[#808080]">{store.nameEn}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-[#0a0a0a] rounded-xl p-3">
                        <p className="text-xs text-[#606060] mb-1">
                          {language === 'ar' ? 'نشاط' : 'Activity'}
                        </p>
                        <p className="text-sm text-white font-medium">
                          {getStoreTypeName(store.type, language) || store.type || (language === 'ar' ? 'غير محدد' : 'Not specified')}
                        </p>
                      </div>
                      <div className="bg-[#0a0a0a] rounded-xl p-3">
                        <p className="text-xs text-[#606060] mb-1">
                          {language === 'ar' ? 'نوع' : 'Type'}
                        </p>
                        <p className={`text-sm font-medium ${V.rowTypeColor}`}>
                          {language === 'ar' ? V.rowTypeAr : V.rowTypeEn}
                        </p>
                      </div>
                      <div className="bg-[#0a0a0a] rounded-xl p-3">
                        <p className="text-xs text-[#606060] mb-1">
                          {language === 'ar' ? 'المالك' : 'Owner'}
                        </p>
                        <p className="text-sm text-white font-medium">
                          {store.owner_name || (language === 'ar' ? 'غير محدد' : 'Not specified')}
                        </p>
                      </div>
                      <div className="bg-[#0a0a0a] rounded-xl p-3">
                        <p className="text-xs text-[#606060] mb-1">
                          {language === 'ar' ? 'تاريخ الطلب' : 'Request Date'}
                        </p>
                        <p className="text-sm text-white font-medium">{formatDate(store.created_at)}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm">
                      {store.owner_email && (
                        <span className="flex items-center gap-1 text-[#808080]">
                          <Mail className="h-4 w-4" />
                          {store.owner_email}
                        </span>
                      )}
                      {store.owner_phone && (
                        <span className="flex items-center gap-1 text-[#808080]">
                          <Phone className="h-4 w-4" />
                          <span dir="ltr">{store.owner_phone}</span>
                        </span>
                      )}
                    </div>

                    {store.description && (
                      <div className="mt-3 p-3 bg-[#0a0a0a] rounded-xl">
                        <p className="text-xs text-[#606060] mb-1">
                          {language === 'ar' ? 'الوصف' : 'Description'}
                        </p>
                        <p className="text-sm text-white">{store.description}</p>
                      </div>
                    )}

                    {(store.status === 'needs_edit' || store.status === 'modification_required') &&
                      (store.edit_reason || store.modification_message) && (
                        <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                          <p className="text-xs text-amber-400 mb-1">
                            {language === 'ar' ? 'التعديل المطلوب:' : 'Required Modification:'}
                          </p>
                          <p className="text-sm text-white">{store.edit_reason || store.modification_message}</p>
                        </div>
                      )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => {
                        setSelectedStore(store);
                        setShowStoreDetails(true);
                      }}
                      variant="outline"
                      className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
                    >
                      <Eye className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'التفاصيل' : 'Details'}
                    </Button>
                    {(store.status === 'pending' ||
                      store.status === 'needs_edit' ||
                      store.status === 'resubmitted' ||
                      store.status === 'edited' ||
                      store.status === 'pending_review') && (
                        <>
                          <Button
                            onClick={() => approveStore(store.id)}
                            disabled={processingStore === store.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            {processingStore === store.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 me-2" />
                                {language === 'ar' ? 'موافقة' : 'Approve'}
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedStore(store);
                              setShowModificationModal(true);
                            }}
                            variant="outline"
                            className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                          >
                            <Edit3 className="h-4 w-4 me-2" />
                            {language === 'ar' ? 'طلب تعديل' : 'Request Edit'}
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedStore(store);
                              setShowRejectModal(true);
                            }}
                            variant="outline"
                            className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                          >
                            <XCircle className="h-4 w-4 me-2" />
                            {language === 'ar' ? 'رفض' : 'Reject'}
                          </Button>
                        </>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Public component ----------
export default function StoreRequestsTab({ storeBusinessType, ...rest }) {
  if (storeBusinessType === null) {
    return <HubView {...rest} />;
  }
  if (storeBusinessType === 'personal' || storeBusinessType === 'company') {
    return <ListView variant={storeBusinessType} language={rest.language} props={rest} />;
  }
  return null;
}
